/**
 * Utilitaires DIVISIBILITÉ partagés par les générateurs 4ème (gen_153 à gen_157).
 *
 * Tout est arithmétique entière EXACTE (aucun flottant) : primalité par
 * division d'essai, décomposition en facteurs premiers, PGCD/PPCM (Euclide),
 * liste des diviseurs. Ces helpers servent la GÉNÉRATION (construction des
 * énoncés). Aucun solveur côté test ne couvre 153 à 157 (solvers/index.js ne
 * les route pas) ; le recalcul indépendant vit dans les tests de revue, qui
 * n'importent JAMAIS ce module (règle d'or anti-tautologie), et chaque gen
 * revérifie ses tirages par un second chemin écrit dans le gen lui-même
 * (énumération des diviseurs, produits), throw sinon. (Revue 2026-09-23.)
 */

/** Les 25 nombres premiers inférieurs à 100 (référence programme 4e). */
export const PREMIERS_100 = [
    2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47,
    53, 59, 61, 67, 71, 73, 79, 83, 89, 97,
];

/** PGCD (Euclide). */
export const pgcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) { [a, b] = [b, a % b]; }
    return a || 1;
};

/** PPCM exact via le PGCD. */
export const ppcm = (a, b) => Math.abs(a * b) / pgcd(a, b);

/** Test de primalité par division d'essai jusqu'à √n. 0 et 1 → non premiers. */
export const estPremier = (n) => {
    if (n < 2) return false;
    if (n < 4) return true;               // 2, 3
    if (n % 2 === 0) return false;
    for (let d = 3; d * d <= n; d += 2) {
        if (n % d === 0) return false;
    }
    return true;
};

/** Plus petit diviseur premier de n (n ≥ 2). Retourne n lui-même s'il est premier. */
export const plusPetitFacteurPremier = (n) => {
    if (n % 2 === 0) return 2;
    for (let d = 3; d * d <= n; d += 2) {
        if (n % d === 0) return d;
    }
    return n;
};

/**
 * Décompose n ≥ 2 en facteurs premiers.
 * @returns {Array<{p:number, exp:number}>} facteurs premiers CROISSANTS avec exposants.
 */
export const decomposer = (n) => {
    const facteurs = [];
    let m = n;
    let d = 2;
    while (d * d <= m) {
        if (m % d === 0) {
            let exp = 0;
            while (m % d === 0) { m /= d; exp++; }
            facteurs.push({ p: d, exp });
        }
        d += (d === 2) ? 1 : 2;
    }
    if (m > 1) facteurs.push({ p: m, exp: 1 });
    return facteurs;
};

/** Reconstruit un entier depuis une liste {p, exp}. */
export const recomposer = (facteurs) =>
    facteurs.reduce((acc, { p, exp }) => acc * (p ** exp), 1);

/** Liste TRIÉE croissante des diviseurs de n (n ≥ 1). */
export const diviseurs = (n) => {
    const petits = [];
    const grands = [];
    for (let d = 1; d * d <= n; d++) {
        if (n % d === 0) {
            petits.push(d);
            if (d !== n / d) grands.push(n / d);
        }
    }
    return petits.concat(grands.reverse());
};

/** Somme des chiffres d'un entier positif (critères de divisibilité 3 et 9). */
export const sommeChiffres = (n) =>
    String(Math.abs(n)).split('').reduce((s, c) => s + Number(c), 0);

// ─────────────────────────────────────────────────
// Formatage KaTeX
// ─────────────────────────────────────────────────

/** Exposant KaTeX : p^exp (exp = 1 → juste p). Ex. {p:2,exp:3} → "2^{3}". */
export const facteurKatex = ({ p, exp }) => (exp === 1 ? String(p) : `${p}^{${exp}}`);

/** Décomposition entière en KaTeX : "2^{3} \times 3 \times 5". */
export const decompositionKatex = (facteurs) =>
    facteurs.map(facteurKatex).join(' \\times ');

/** Fraction KaTeX inline avec délimiteurs ("$\dfrac{a}{b}$"), entier si b=1. */
export const fracKatex = (num, den) =>
    (den === 1 ? String(num) : `$\\dfrac{${num}}{${den}}$`);
